import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE, tenantEnabledAppLocales, type AppLocale } from "../i18n/locale-state";
import { INITIAL_ENABLED_LOCALES, localeMetadata } from "../i18n/locale-registry";
import { useLocale } from "../i18n/LocaleProvider";
import { foldLocaleText } from "../i18n/intl";
import { profileApi } from "../api/endpoints";
import { Icon } from "../icons";

const endonym = (locale: AppLocale): string => localeMetadata(locale)?.nativeName ?? locale;
const englishName = (locale: AppLocale): string => localeMetadata(locale)?.englishName ?? locale;

// Initial rollout: every exposed non-source pack is machine/unreviewed.
// The manifest guard in language-switch.test.tsx keeps this public policy
// aligned without shipping private reviewer/provenance records to the client.
export const isBetaLocale = (locale: AppLocale): boolean =>
  locale !== DEFAULT_LOCALE && INITIAL_ENABLED_LOCALES.includes(locale);

export const languageOptionLabel = (locale: AppLocale): string =>
  `${endonym(locale)}${isBetaLocale(locale) ? " · Beta" : ""}`;

export const usesExpandedLanguageSelector = (locales: readonly AppLocale[]): boolean => locales.length > 2;
export const usesLanguageSearch = (locales: readonly AppLocale[]): boolean => locales.length > 7;

export const matchesLanguageQuery = (code: AppLocale, query: string, locale: AppLocale): boolean => {
  const trimmed = query.trim();
  if (!trimmed) return true;
  const needle = foldLocaleText(trimmed, locale);
  // Tags are canonical protocol values, not Turkish display-name text.
  return code.toLowerCase().includes(trimmed.toLowerCase())
    || [endonym(code), englishName(code)].some((value) => foldLocaleText(value, locale).includes(needle));
};

export interface LanguageSwitcherProps {
  authed?: boolean;
  variant?: "full" | "compact" | "footer";
  enabledLocales?: readonly AppLocale[];
}

type OpenMode = "desktop" | "mobile" | null;

export const LanguageSwitcher = ({
  authed = false,
  variant = "full",
  enabledLocales,
}: LanguageSwitcherProps): React.ReactElement => {
  const { t } = useTranslation("shell");
  const { locale, changeLocale, switching } = useLocale();
  const availableLocales = React.useMemo(
    () => tenantEnabledAppLocales(enabledLocales),
    [enabledLocales],
  );
  const [openMode, setOpenMode] = React.useState<OpenMode>(null);
  const [query, setQuery] = React.useState("");
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [focusedLocale, setFocusedLocale] = React.useState<AppLocale>(locale);
  const [returnFocusMode, setReturnFocusMode] = React.useState<OpenMode>(null);
  const menuId = React.useId();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const desktopTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const mobileTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const optionRefs = React.useRef(new Map<AppLocale, HTMLButtonElement>());
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const label = t("language.switcherAria");
  const footer = variant === "footer";

  const filteredLocales = React.useMemo(() => {
    return availableLocales.filter((code) => matchesLanguageQuery(code, query, locale));
  }, [availableLocales, query, locale]);

  const close = React.useCallback((restoreFocus = false) => {
    setReturnFocusMode(restoreFocus ? openMode : null);
    setOpenMode(null);
    setQuery("");
    setLoadFailed(false);
  }, [openMode]);

  React.useLayoutEffect(() => {
    // A locale switch disables the trigger. Restore only after React has
    // committed the enabled DOM, not in an animation frame racing that commit.
    if (openMode || switching || !returnFocusMode) return;
    const trigger = returnFocusMode === "mobile" ? mobileTriggerRef.current : desktopTriggerRef.current;
    if (!trigger || trigger.disabled) return;
    setReturnFocusMode(null);
    trigger.focus();
  }, [openMode, switching, returnFocusMode]);

  const pick = React.useCallback(
    async (next: AppLocale) => {
      if (next === locale || switching) {
        close(true);
        return;
      }
      setLoadFailed(false);
      try {
        await changeLocale(next);
      } catch {
        setLoadFailed(true);
        return;
      }
      close(true);
      if (authed) {
        try {
          await profileApi.updateOwn({ locale: next });
        } catch {
          // Cookie persistence already succeeded; the profile can catch up later.
        }
      }
    },
    [locale, switching, changeLocale, close, authed],
  );

  const open = React.useCallback((mode: Exclude<OpenMode, null>) => {
    setReturnFocusMode(null);
    setQuery("");
    setLoadFailed(false);
    setFocusedLocale(locale);
    setOpenMode(mode);
  }, [locale]);

  React.useEffect(() => {
    if (!openMode) return;
    const onDocPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) close(false);
    };
    const onDocKey = (event: KeyboardEvent) => {
      // Escape can cancel an IME candidate instead of dismissing the menu.
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onDocKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onDocKey);
    };
  }, [openMode, close]);

  React.useLayoutEffect(() => {
    if (!openMode) return;
    // Focus before paint: the first ArrowDown must reach the menu, not the
    // trigger or a later animation-frame focus reset.
    if (usesLanguageSearch(availableLocales)) searchRef.current?.focus();
    else optionRefs.current.get(locale)?.focus();
  }, [openMode, availableLocales, locale]);

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Candidate navigation belongs to the IME, including its 229 fallback.
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) || filteredLocales.length === 0) return;
    event.preventDefault();
    const currentIndex = filteredLocales.findIndex((code) => optionRefs.current.get(code) === document.activeElement);
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? filteredLocales.length - 1
        : event.key === "ArrowDown"
          ? (currentIndex + 1 + filteredLocales.length) % filteredLocales.length
          : (currentIndex - 1 + filteredLocales.length) % filteredLocales.length;
    optionRefs.current.get(filteredLocales[nextIndex])?.focus();
  };

  const renderMenu = (mode: Exclude<OpenMode, null>) => {
    if (openMode !== mode) return null;
    const testPrefix = mode === "mobile" ? "language-switcher-mobile" : "language-switcher";
    const menu = (
      <div
        ref={menuRef}
        className={`language-popover${mode === "mobile" ? " is-mobile" : ""}${footer ? " is-footer" : ""}`}
        id={menuId}
        role="dialog"
        aria-label={label}
        data-testid={`language-switcher-${mode}-menu`}
        onKeyDown={onMenuKeyDown}
      >
        {usesLanguageSearch(availableLocales) && (
          <label className="language-search">
            <Icon name="search" size={16} aria-hidden="true" />
            <span className="sr-only">{label}</span>
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              aria-label={label}
              placeholder={label}
              autoComplete="off"
              data-testid={`language-switcher-${mode}-search`}
            />
          </label>
        )}
        {loadFailed && <p role="alert">{t("common:sharedState.errorMessage")}</p>}
        <div className="language-options" role="listbox" aria-label={label}>
          {filteredLocales.map((code) => {
            const active = code === locale;
            return (
              <button
                key={code}
                ref={(node) => {
                  if (node) optionRefs.current.set(code, node);
                  else optionRefs.current.delete(code);
                }}
                id={`${menuId}-option-${code}`}
                type="button"
                role="option"
                aria-selected={active}
                tabIndex={code === (filteredLocales.includes(focusedLocale) ? focusedLocale : filteredLocales[0]) ? 0 : -1}
                onFocus={() => setFocusedLocale(code)}
                disabled={switching}
                lang={code}
                dir="auto"
                className="language-option"
                data-testid={`${testPrefix}-${code}`}
                onClick={() => void pick(code)}
              >
                <span className="language-option-check" aria-hidden="true">
                  {active ? <Icon name="check" size={16} stroke={2.6} /> : null}
                </span>
                <span className="language-option-copy">
                  <span className="language-option-name">{endonym(code)}</span>
                  <span className="language-option-meta" dir="ltr">{englishName(code)} · {code}</span>
                </span>
                {isBetaLocale(code) && <span className="language-beta">Beta</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
    // A fixed child of the blurred/transformed mobile header uses that
    // header as its containing block. The shared menu belongs to the viewport.
    return mode === "mobile" && typeof document !== "undefined"
      ? createPortal(menu, document.body)
      : menu;
  };

  return (
    <div
      ref={rootRef}
      className={`lang-switch-root lang-switch-${variant}`}
      data-testid={footer ? "language-switcher-footer" : undefined}
      onBlur={(event) => {
        const target = event.relatedTarget as Node | null;
        if (target && !event.currentTarget.contains(target) && !menuRef.current?.contains(target)) close(false);
      }}
    >
      {footer && <span className="language-footer-label">{label}</span>}
      <button
        ref={desktopTriggerRef}
        type="button"
        className="lang-switch lang-switch-pill language-trigger"
        aria-label={label}
        aria-haspopup="dialog"
        aria-controls={openMode === "desktop" ? menuId : undefined}
        aria-expanded={openMode === "desktop"}
        disabled={switching}
        data-testid={footer ? "language-switcher-footer-trigger" : "language-switcher"}
        onClick={() => openMode === "desktop" ? close(false) : open("desktop")}
      >
        <Icon name="globe" size={17} aria-hidden="true" />
        <span className="language-trigger-name" lang={locale}>{endonym(locale)}</span>
        {variant === "full" && <span className="language-trigger-code" dir="ltr">{locale}</span>}
        <Icon name="chevronLeft" size={14} aria-hidden="true" className="language-trigger-chevron" />
      </button>
      {renderMenu("desktop")}

      {!footer && (
        <button
          ref={mobileTriggerRef}
          type="button"
          className="lang-switch-icon nav-icon-btn"
          aria-label={label}
          aria-haspopup="dialog"
          aria-controls={openMode === "mobile" ? menuId : undefined}
          aria-expanded={openMode === "mobile"}
          disabled={switching}
          data-testid="language-switcher-mobile-trigger"
          onClick={() => openMode === "mobile" ? close(false) : open("mobile")}
        >
          <Icon name="globe" />
        </button>
      )}
      {renderMenu("mobile")}
    </div>
  );
};

export default LanguageSwitcher;
